import {
  fetchVideoMetadataAndNextResources,
  VIDEO_PLAYER_RESOURCES_ACTION_PREFIX,
} from '@redux/common/thunks';
import { Expiration, SongWithMetadata } from '@redux/songStorage/types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import localDB, { MS_IN_DAY } from 'lib/IndexedDB/RessourcesDB';
import { Song } from 'src/@types/api-types';

import { generateLicenseInfo } from 'services/helpers/licenseInfo';
import client from 'services/networking/client';
import { getLyrics } from 'services/networking/lyrics';
import { getB2bVideoMetadata, getB2cVideoMetadata, getSong } from 'services/networking/songs';
import { getSwipeColor } from 'services/networking/swipe';
import { SCOPE } from 'shared-types/constants';
import { Lyrics } from 'shared-types/lyrics';
import { PLAYER_TYPE, VideoMetadata } from 'shared-types/video-metadata';
import { SwipeColors } from 'types/swipeColors';

import { setFadeDuration } from './slice';
import { FetchSongPayload } from './types';

export const fetchSongAndNextResources = createAsyncThunk<Song, FetchSongPayload>(
  `${VIDEO_PLAYER_RESOURCES_ACTION_PREFIX}/fetchSongDetails`,
  async ({ songId, scope, userId, isStorageActivated }, thunkApi) => {
    const song = isStorageActivated
      ? await localDB.fetchOrGetData<Song>(songId, getSong)
      : await getSong(songId);

    const fadeDuration = song.fadeDuration || 300; // fade can be undefined for downloaded songs
    thunkApi.dispatch(setFadeDuration(fadeDuration));

    const getVideoMetadata = scope === SCOPE.B2C ? getB2cVideoMetadata : getB2bVideoMetadata;

    thunkApi.dispatch(
      fetchVideoMetadataAndNextResources({
        getVideoMetadata: (videoFilename: string, userId: string) =>
          isStorageActivated
            ? localDB.fetchOrGetData<VideoMetadata>(
                `/video-metadata?scope=${SCOPE.B2B}`,
                (url: string, data: object) => client.post(url, data),
                generateLicenseInfo(videoFilename, userId),
              )
            : getVideoMetadata(generateLicenseInfo(videoFilename, userId)),
        videoFilename: song.videoFilename,
        colorSetting: song.colorSetting,
        userId,
        isStorageActivated,
      }),
    );
    return song;
  },
);

export const fetchBtoBSongMetadata = async (
  song: Song,
  userId: string,
): Promise<SongWithMetadata> => {
  // Fetching and Saving all ressources in the local DB if needed
  await await localDB.fetchOrGetData<Song>(song.id, getSong);
  const videoMetadata = await localDB.fetchOrGetData<VideoMetadata>(
    `/video-metadata?scope=${SCOPE.B2B}`,
    (url: string, data: object) => client.post(url, data),
    generateLicenseInfo(song.videoFilename, userId),
  );

  if (videoMetadata.playerType === PLAYER_TYPE.MP3_KBP) {
    await localDB.fetchOrGetData<Lyrics>(videoMetadata.lyricsJsonUrl, getLyrics);
    await localDB.fetchOrGetData<SwipeColors>(song.colorSetting, getSwipeColor);
  }
  const creationDate: Expiration = { expiration: Date.now() + MS_IN_DAY };
  const metadata = Object.assign(videoMetadata, song, creationDate);

  return metadata as SongWithMetadata;
};
